import React, { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { useStreams } from '../app/StreamsProvider.jsx'
import { NotificationTypes, useNotifications } from '../hooks/useNotifications.jsx'
import '../activity-stream/styles.css'
import '../styles/live-stream.css'

const formatTimestamp = (value) => {
  if (!value) {
    return '—'
  }
  const date = typeof value === 'string' ? new Date(value) : new Date(Number(value))
  if (Number.isNaN(date.getTime())) {
    return '—'
  }
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`
}

const formatLag = (lagMs) => {
  if (lagMs === null || lagMs === undefined) {
    return '—'
  }
  if (lagMs < 1000) {
    return `${lagMs} ms`
  }
  const seconds = lagMs / 1000
  if (seconds < 60) {
    return `${seconds.toFixed(1)} s`
  }
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = (seconds % 60).toFixed(0)
  return `${minutes}m ${remainingSeconds}s`
}

const formatJsonPayload = (payload) => {
  if (!payload) {
    return '—'
  }
  try {
    const parsed = typeof payload === 'string' ? JSON.parse(payload) : payload
    return JSON.stringify(parsed, null, 2)
  } catch {
    if (typeof payload === 'string') {
      return payload
    }
    return JSON.stringify(payload, null, 2)
  }
}

const LiveStreamPanel = () => {
  const [payloadMode, setPayloadMode] = useState('json')
  const { notify } = useNotifications()
  const lastErrorRef = useRef(null)
  const lastStatusRef = useRef(null)
  const hasInitializedFilters = useRef(false)

  const { liveMonitorStream } = useStreams()
  const {
    events,
    connectionState,
    error,
    lagMs,
    isStale,
    metrics,
    connect,
    retry,
    clearEvents,
    isStreaming,
    isAuthenticated,
    setInstance,
    setStatement,
  } = liveMonitorStream

  // Clear instance and statement filters before first connection
  // This runs synchronously on mount, before the stream connects
  if (!hasInitializedFilters.current) {
    hasInitializedFilters.current = true
    // Clear session storage to ensure no filters (using live-monitor- prefix)
    sessionStorage.removeItem('live-monitor-webui-active-instance')
    sessionStorage.removeItem('live-monitor-webui-active-statement')
  }

  // Ensure filters stay cleared
  useEffect(() => {
    setInstance('')
    setStatement('')
  }, [setInstance, setStatement])

  useEffect(() => {
    if (!error) {
      lastErrorRef.current = null
      return
    }

    const errorKey = `${error.status ?? 'unknown'}-${error.message ?? 'error'}`
    if (lastErrorRef.current === errorKey) {
      return
    }
    lastErrorRef.current = errorKey

    notify({
      type:
        error.status === 401 || error.status === 403
          ? NotificationTypes.ERROR
          : NotificationTypes.WARNING,
      title: 'Live stream interrupted',
      message: error.message ?? 'Connection closed unexpectedly. Retry when ready.',
      autoDismissMs: 8_000,
    })
  }, [error, notify])

  useEffect(() => {
    if (connectionState === lastStatusRef.current) {
      return
    }
    lastStatusRef.current = connectionState

    if (connectionState === 'connected') {
      notify({
        type: NotificationTypes.SUCCESS,
        title: 'Live stream reconnected',
        message: 'Monitoring resumed. Stale banner will clear automatically.',
        autoDismissMs: 4_000,
      })
    }
  }, [connectionState, notify])

  const statusLabel = useMemo(() => {
    switch (connectionState) {
      case 'connected':
        return 'Connected'
      case 'connecting':
        return 'Connecting…'
      case 'reconnecting':
        return 'Reconnecting…'
      case 'error':
        return 'Error'
      case 'stopped':
        return 'Stopped'
      default:
        return 'Idle'
    }
  }, [connectionState])

  if (!isAuthenticated) {
    return (
      <div className="as-app">
        <main className="as-layout" style={{ display: 'block' }}>
          <section className="as-panel">
            <p style={{ margin: 0, textAlign: 'center' }}>Authenticate to begin streaming statement events.</p>
            <p style={{ margin: '1rem 0', textAlign: 'center', fontSize: '0.9rem', color: 'var(--as-text-muted)' }}>
              Please select an API key from the dropdown in the header.
            </p>
          </section>
        </main>
      </div>
    )
  }

  return (
    <div className="as-app">
      <main className="as-layout" style={{ display: 'block' }}>
        <section className="as-panel live-stream">
      <div className="live-stream__header">
        <h3>Live Monitor</h3>
        <div style={{ fontSize: '0.85rem', color: 'var(--as-text-muted)' }}>
          Status: {statusLabel} {connectionState === 'error' && error ? `(${error.status ?? 'Unknown'})` : ''}
        </div>
      </div>

      {isStale ? (
        <div className="live-stream__stale-banner" role="status">
          <strong>Stream appears stale.</strong> Last event arrived {formatLag(lagMs)} ago.
        </div>
      ) : null}

      {metrics.nextRetry ? (
        <div className="live-stream__retry-banner" role="status">
          Retrying in {Math.ceil(metrics.nextRetry.delay / 1000)} seconds (attempt {metrics.nextRetry.attempt}).
        </div>
      ) : null}

      {error ? (
        <div className="live-stream__error" role="alert">
          <div>
            <strong>{error.status ?? ''}</strong> {error.message ?? 'Unexpected error' }
          </div>
          <button type="button" onClick={retry}>
            Retry now
          </button>
        </div>
      ) : null}

      <div className="live-stream__controls">
        <div className="live-stream__buttons">
          <button type="button" onClick={clearEvents} className="secondary">
            Clear events
          </button>
        </div>
      </div>

      <div className="live-stream__toolbar">
        <span>
          {events.length} event{events.length === 1 ? '' : 's'} captured · Lag {formatLag(lagMs)}
        </span>
        <div className="live-stream__toggle" role="radiogroup" aria-label="Payload format">
          <button
            type="button"
            className={clsx({ active: payloadMode === 'json' })}
            onClick={() => setPayloadMode('json')}
          >
            JSON
          </button>
          <button
            type="button"
            className={clsx({ active: payloadMode === 'csv' })}
            onClick={() => setPayloadMode('csv')}
          >
            CSV
          </button>
        </div>
      </div>

      <div className="live-stream__events" role="log" aria-live="polite">
        {events.length === 0 ? (
          <div className="live-stream__placeholder">
            <p>No statement events received yet. Events will appear here in real time.</p>
          </div>
        ) : (
          events.map((event) => (
            <article
              key={event.id}
              className={clsx('live-stream__event', {
                'live-stream__event--stale': event.isStale,
              })}
            >
              <header className="live-stream__event-header">
                <div className="live-stream__event-meta">
                  <strong>{event.statementName ?? 'Unknown statement'}</strong>
                  <span>{event.instanceName ?? '—'}</span>
                </div>
                <div className="live-stream__event-timing">
                  <time dateTime={event.timestamp ?? ''}>{formatTimestamp(event.timestamp)}</time>
                  <span className="live-stream__event-lag">Lag {formatLag(event.lagMs)}</span>
                </div>
              </header>
              <pre className="live-stream__event-payload">
                {payloadMode === 'json'
                  ? formatJsonPayload(event.raw?.data ?? event)
                  : event.raw?.data?.csvPayload ?? '—'}
              </pre>
            </article>
          ))
        )}
      </div>
        </section>
      </main>
    </div>
  )
}

export default LiveStreamPanel
